import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Tag,
  ShieldCheck,
  Truck,
  FlaskConical,
  Bluetooth,
} from "lucide-react";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import {
  TriCompareTable,
  SpecTable,
  AnswerBlock,
  Prose,
  ReviewStamp,
} from "@/components/site/ContentPrimitives";
import DecisionTree from "@/components/diagrams/DecisionTree";
import { useSEO, useJsonLd, SITE_URL, breadcrumbSchema, faqSchema } from "@/lib/seo";
import { GoAndTrackLink, linkifyGoAndTrack } from "@/lib/goAndTrack";

const IMG = "/images/ll100-hero.jpg";

const FAQS = [
  {
    q: "Is a disposable GPS tracker actually a GPS device?",
    a: "Only if it contains a GNSS receiver. Cellular / GNSS disposable trackers include real GPS hardware. Ultra-thin BLE disposable smart labels — often searched for as 'disposable GPS trackers' — do not include GPS; they resolve position through BLE gateway detections.",
  },
  {
    q: "How accurate is a disposable GPS tracker?",
    a: "Cellular / GNSS class devices provide open-sky GPS accuracy in the low-metre range. BLE labels resolve to the venue / gateway level, which can range from a few metres indoors up to tens of metres depending on the gateway network.",
  },
  {
    q: "How long does a disposable GPS tracker last?",
    a: "Cellular / GNSS disposable devices typically report for 10–180 days depending on how frequently they sample and transmit. Ultra-thin BLE labels typically report for up to 40 days on a single non-replaceable battery.",
  },
  {
    q: "Can I use a disposable GPS tracker on air freight?",
    a: "Suitability depends on the specific device, its battery chemistry, airline requirements, and route. Product-specific approvals should be validated for the operating lane rather than relying on category-level claims.",
  },
  {
    q: "What conditions can a disposable GPS tracker monitor?",
    a: "Cellular / GNSS class devices commonly include temperature, humidity, shock / vibration, and ambient-light sensors. Ultra-thin BLE labels are usually location-only unless the datasheet lists additional sensors.",
  },
  {
    q: "How do I dispose of a used disposable GPS tracker?",
    a: "The device contains electronic components and a battery. End-of-life treatment depends on product construction and local e-waste and battery-waste regulations — follow the device-specific disposal route provided with the shipment.",
  },
  {
    q: "Is a disposable GPS tracker cheaper than a reusable GPS tracker?",
    a: "Per-shipment yes, per-trip on a well-recovered reusable fleet possibly no. The right comparison is total-cost-per-shipment-tracked, including recovery, charging, inventory, and write-off costs on the reusable side.",
  },
  {
    q: "Can I integrate a disposable GPS tracker with my TMS?",
    a: "Yes — live data flows into GoAndTrack, which exposes REST APIs and webhooks for integration into TMS, WMS, and ERP systems.",
  },
];

export default function DisposableGpsTracker() {
  useSEO({
    title:
      "Disposable GPS Tracker for Packages | Single-Use GPS Tracking Stickers",
    description:
      "Disposable GPS trackers for packages — single-use, peel-and-stick tracking devices for high-value shipments, logistics pilots, and temporary shipment monitoring.",
    canonical: "/disposable-gps-tracker",
  });

  useJsonLd("service-disposable-gps", {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Disposable GPS Tracker",
    alternateName: [
      "Disposable GPS Tracker for Packages",
      "Disposable GPS Tracking Stickers",
      "Single-Use GPS Tracker",
      "Disposable Smart Label",
    ],
    serviceType: "Shipment Tracking Devices / Package Tracking Device",
    category: "Shipment Tracking Devices / Package Tracking Device",
    description:
      "Single-use, peel-and-stick disposable GPS trackers for packages and shipments. Designed for one shipment journey, with no recovery, recharging, or pairing required.",
    provider: {
      "@type": "Organization",
      name: "Disposable Smart Labels",
      url: SITE_URL,
    },
    url: `${SITE_URL}/disposable-gps-tracker`,
    audience: {
      "@type": "BusinessAudience",
      audienceType: "Logistics, supply chain, and operations teams",
    },
    areaServed: [
      { "@type": "Place", name: "United Kingdom" },
      { "@type": "Place", name: "European Union" },
    ],
  });

  useJsonLd(
    "breadcrumb-dgt",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Disposable Package Tracker", url: "/disposable-package-tracker" },
      { name: "Disposable GPS Tracker" },
    ]),
  );

  useJsonLd("faq-dgt", faqSchema(FAQS));

  return (
    <div data-testid="page-disposable-gps">
      <PageHero
        eyebrow="Also known as"
        title="Disposable GPS Trackers for Packages and Shipments"
        subtitle="Single-use, peel-and-stick tracking devices — also called disposable GPS tracking stickers — for high-value packages, temporary shipment monitoring, and structured logistics pilots."
      >
        <Link to="/request-a-trial" className="btn-primary" data-testid="page-cta-trial">
          Speak to an expert <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/disposable-smart-labels"
          className="btn-secondary"
          data-testid="page-cta-category"
        >
          Category overview
        </Link>
      </PageHero>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <p className="eyebrow mb-4">Same product, different vocabulary</p>
            <h2 className="h2">
              &quot;Disposable GPS tracker&quot; is what most buyers call a{" "}
              <Link
                to="/disposable-smart-labels"
                className="text-brand hover:underline"
              >
                disposable smart label
              </Link>
              .
            </h2>
            <p className="lead mt-5">
              The term <strong className="text-ink">disposable GPS tracker</strong>{" "}
              is widely used by operations and supply chain teams searching for a
              tracking device they can apply to a package and discard at end of
              journey. Industry terminology calls this a disposable smart label.
              The product, the use case, and the operational model are the same.
            </p>
            <ul className="mt-8 space-y-3.5">
              {[
                "Single-use, applied directly to the package — no enclosure required.",
                "Sometimes called a disposable GPS tracking sticker because of the peel-and-stick form factor.",
                "No reverse logistics. No hardware recovery. No fleet management.",
                "Operational visibility delivered through GoAndTrack, including live shipment monitoring and exception alerts.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px] text-ink">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-brand shrink-0" />
                  {linkifyGoAndTrack(t)}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-line bg-white">
              <div className="aspect-[5/4] overflow-hidden">
                <img
                  src={IMG}
                  alt="GoAndTrack disposable smart label — product view with peel-here strip and barcode"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="p-6 border-t border-line">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                  Common search terms
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    "Disposable GPS tracker",
                    "GPS tracking sticker",
                    "Single-use GPS tracker",
                    "Disposable package tracker",
                    "Disposable smart label",
                  ].map((t) => (
                    <span key={t} className="badge-line">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-surface-subtle border-y border-line">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Technical note</p>
            <h2 className="h2">
              Not all &quot;disposable GPS trackers&quot; are actually GPS.
            </h2>
            <p className="lead mt-5">
              Most modern single-use shipment trackers use{" "}
              <Link to="/bluetooth-smart-labels" className="text-brand hover:underline">
                Bluetooth Low Energy (BLE)
              </Link>{" "}
              rather than dedicated GPS hardware. The result is what buyers
              actually want — package-level location and journey data — without
              the power, cost, and size penalties of full-GPS chipsets.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
            {[
              {
                k: "What buyers ask for",
                v: '"A disposable GPS tracker we can stick on a package, get location data, and throw away."',
              },
              {
                k: "What gets delivered",
                v: "A thin, single-use BLE smart label that integrates with mobile, gateway, and partner-network detection — delivering location, dwell, and exception data.",
              },
              {
                k: "Why this matters",
                v: "BLE-based disposable labels hit the same operational outcome at a fraction of the cost, weight, and complexity of true cellular GPS trackers.",
              },
            ].map((b) => (
              <div key={b.k} className="bg-white p-7 lg:p-8">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink-subtle">
                  {b.k}
                </p>
                <p className="mt-3 text-[15px] text-ink-muted">{b.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="max-w-2xl mb-12">
            <p className="eyebrow mb-4">Use cases</p>
            <h2 className="h2">
              Where disposable GPS trackers (and tracking stickers) fit.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
            {[
              {
                icon: ShieldCheck,
                k: "High-value packages",
                v: "Independent journey evidence for shipments where loss or delay carries serious commercial weight.",
                to: "/smart-labels-for-high-value-shipments",
              },
              {
                icon: Truck,
                k: "End-to-end shipment monitoring",
                v: "Lane visibility for road, air, and multi-leg movements where reusable hardware can't be recovered.",
                to: "/smart-labels-for-logistics",
              },
              {
                icon: FlaskConical,
                k: "Logistics pilots",
                v: "Stand up shipment-tracking pilots in days. No CapEx commitment to gather operational evidence.",
                to: "/smart-labels-for-pilot-projects",
              },
              {
                icon: Tag,
                k: "Temporary tracking",
                v: "Single-trip cover for trade shows, events, returns, and one-off shipments where reusable trackers are impractical.",
                to: "/disposable-smart-labels",
              },
            ].map((u) => (
              <Link
                key={u.k}
                to={u.to}
                className="group bg-white p-7 lg:p-8 hover:bg-surface-subtle transition-colors"
                data-testid={`gps-usecase-${u.k.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="flex items-start justify-between gap-6">
                  <div>
                    <u.icon className="h-5 w-5 text-brand" />
                    <h3 className="h3 mt-4">{u.k}</h3>
                    <p className="body-text mt-2 text-sm">{u.v}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-ink-subtle mt-1 group-hover:text-brand transition-colors shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-ink text-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/60 mb-4">
              How it works
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.1]">
              Apply the sticker. Dispatch the package. Track the journey.
            </h2>
            <p className="mt-6 text-white/70 max-w-xl">
              Each disposable GPS tracking sticker is designed for one shipment.
              Apply it on the dispatch floor, send the package, and watch the
              data flow into <GoAndTrackLink /> — without ever needing the device back.
            </p>
            <Link
              to="/gps-tracker-for-packages"
              className="mt-8 inline-flex items-center gap-2 text-white border-b border-white/40 hover:border-white pb-1"
              data-testid="gps-link-package"
            >
              See the package-level view <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { n: "01", k: "Peel", v: "Single-use disposable tracker, ready to deploy." },
              { n: "02", k: "Stick", v: "Apply to the package, carton, or pallet on the dispatch floor." },
              { n: "03", k: "Track", v: "Live location and shipment monitoring through GoAndTrack." },
            ].map((s) => (
              <div key={s.n} className="bg-ink p-7">
                <p className="font-mono text-xs text-white/60">{s.n}</p>
                <h3 className="font-heading text-xl font-semibold mt-3">{s.k}</h3>
                <p className="mt-2 text-sm text-white/70">{linkifyGoAndTrack(s.v)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section border-t border-line">
        <div className="container-x">
          <Prose>
            <h2 className="h2">Technology behind &quot;disposable GPS tracker&quot;</h2>
            <AnswerBlock>
              Buyer language groups all single-use trackers as
              &quot;disposable GPS trackers,&quot; but only cellular /
              GNSS-class devices actually contain a GPS receiver. Here is
              how the three primary architectures compare across the
              things logistics teams care about.
            </AnswerBlock>
            <TriCompareTable
              headers={["BLE smart label", "Cellular + GNSS", "Checkpoint (RFID/NFC)"]}
              rows={[
                {
                  q: "Contains real GPS/GNSS?",
                  values: ["No", "Yes", "No"],
                },
                {
                  q: "Location precision",
                  values: [
                    "Venue / gateway level",
                    "GPS-grade in open sky",
                    "Discrete node events",
                  ],
                },
                {
                  q: "Sensor breadth",
                  values: [
                    "Location-only (usually)",
                    "Temp / humidity / shock / light",
                    "None on tag",
                  ],
                },
                {
                  q: "Battery life",
                  values: ["Up to ~40 days", "10 – 180+ days by config", "Passive"],
                },
                {
                  q: "Independent of external network",
                  values: ["No", "Yes", "No"],
                },
                {
                  q: "Typical unit cost",
                  values: ["Lowest", "Higher", "Very low"],
                },
              ]}
            />
            <p className="body-text mt-6">
              Deeper technical comparison in{" "}
              <a
                href="/gps-vs-ble-vs-cellular"
                className="underline decoration-cobalt/40 hover:decoration-cobalt"
              >
                GPS vs BLE vs Cellular Package Tracking
              </a>
              .
            </p>

            <h2 className="h2 mt-16">Reference specification — cellular + GNSS disposable label</h2>
            <p className="body-text mt-3">
              The cellular / GNSS class of disposable tracker is the one
              most accurately described as a &quot;disposable GPS
              tracker.&quot; Representative specifications:
            </p>
            <SpecTable
              title="4G cellular + GNSS disposable label"
              rows={[
                { k: "Cellular", v: "4G LTE / 2G fallback" },
                { k: "SIM", v: "Global eSIM" },
                { k: "GNSS", v: "GPS / GLONASS / BDS / QZSS" },
                { k: "Assisted position", v: "Wi-Fi + BLE" },
                { k: "Condition sensors", v: "Temperature, humidity, shock/vibration, ambient light" },
                { k: "Battery life", v: "10 – 180+ days depending on reporting configuration" },
                { k: "Dimensions", v: "101 × 177 × 4.7 mm" },
                { k: "Weight", v: "Approximately 34 g" },
              ]}
              note="Air-freight suitability depends on the specific device, battery chemistry, airline, and route. Validate for the operating lane."
            />

            <h2 className="h2 mt-16">Choosing between BLE and cellular / GNSS</h2>
            <DecisionTree />

            <h2 className="h2 mt-16">Frequently asked questions</h2>
            <div className="mt-4 divide-y divide-line border-t border-b border-line">
              {FAQS.map(({ q, a }) => (
                <details
                  key={q}
                  className="group py-4"
                  data-testid={`dgt-faq-${q.slice(0, 30).replace(/[^a-z0-9]/gi, "-")}`}
                >
                  <summary className="cursor-pointer text-ink font-medium text-[15px] list-none flex items-start justify-between gap-4">
                    <span>{q}</span>
                    <span className="text-ink-muted transition-transform group-open:rotate-45 text-xl leading-none shrink-0">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-[15px] leading-relaxed text-ink-muted">
                    {a}
                  </p>
                </details>
              ))}
            </div>

            <ReviewStamp
              date="February 2026"
              basis="Manufacturer specifications for the deployed cellular/GNSS and BLE label classes; direct deployment experience across UK/EU logistics lanes."
            />
          </Prose>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow mb-4">Related reading</p>
            <h2 className="h2">Continue exploring.</h2>
          </div>
          <div className="lg:col-span-7 border border-line bg-white">
            <div className="divide-y divide-line">
              {[
                { to: "/disposable-package-tracker", label: "Disposable Package Tracker — complete category guide" },
                { to: "/gps-vs-ble-vs-cellular", label: "GPS vs BLE vs Cellular — technology comparison" },
                { to: "/disposable-smart-labels", label: "Disposable Smart Labels — category overview" },
                { to: "/gps-tracker-for-packages", label: "GPS Tracker for Packages — package-level tracking" },
                { to: "/bluetooth-smart-labels", label: "Bluetooth Smart Labels — BLE technology explained" },
                { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable Trackers — full comparison" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="flex items-center justify-between px-6 py-4 text-sm font-medium text-ink hover:text-brand transition-colors"
                  data-testid={`gps-related-${l.to.replace(/\//g, "")}`}
                >
                  {l.label} <ArrowRight className="h-4 w-4" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTAStrip
        title="See a disposable GPS tracker on a real shipment."
        body="Tell us about a package or lane — we'll run a structured pilot through GoAndTrack."
      />
    </div>
  );
}
